import React from 'react'
import { useNavigate } from 'react-router-dom'

interface Message {
  type: 'success' | 'error' | 'info'
  text: string
}

interface SettingsPageTemplateProps {
  title: string
  description: string
  message?: Message | null
  children: React.ReactNode
  actionButton?: {
    label: string
    icon?: React.ReactNode
    onClick: () => void
  }
  loading?: boolean
}

/**
 * Standard template for all settings pages
 * Based on Deal Status Management page design
 */
export const SettingsPageTemplate: React.FC<SettingsPageTemplateProps> = ({
  title,
  description,
  message,
  children,
  actionButton,
  loading = false
}) => {
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header Section */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 mb-4 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Settings
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          {title}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {description}
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-6 p-4 rounded-md ${getMessageClasses(message.type)}`}>
          {message.text}
        </div>
      )}

      {/* Action Button */}
      {actionButton && (
        <div className="mb-6">
          <button
            onClick={actionButton.onClick}
            className="flex items-center px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
          >
            {actionButton.icon && <span className="mr-2">{actionButton.icon}</span>}
            {actionButton.label}
          </button>
        </div>
      )}

      {/* Main Content */}
      {children}
    </div>
  )
}

/**
 * Standard settings card component
 */
interface SettingsCardProps {
  title?: string
  children: React.ReactNode
  className?: string
}

export const SettingsCard: React.FC<SettingsCardProps> = ({ 
  title, 
  children, 
  className = '' 
}) => {
  return (
    <div className={`bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="p-6">
        {title && (
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>
  )
}

/**
 * Standard info tip component
 */
interface InfoTipProps {
  children: React.ReactNode
  type?: 'info' | 'warning' | 'success'
}

export const InfoTip: React.FC<InfoTipProps> = ({ children, type = 'info' }) => {
  const typeClasses = {
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
    warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200',
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
  }

  return (
    <div className={`mb-4 p-4 border rounded-lg ${typeClasses[type]}`}>
      <p className="text-sm">
        💡 <strong>Tip:</strong> {children}
      </p>
    </div>
  )
}

/**
 * Standard back button component (standalone)
 */
export const BackToSettingsButton: React.FC = () => {
  const navigate = useNavigate()
  
  return (
    <button
      onClick={() => navigate('/dashboard/settings')}
      className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 mb-4 transition-colors"
    >
      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      Back to Settings
    </button>
  )
}

/**
 * Helper function to get message styling classes
 */
function getMessageClasses(type: 'success' | 'error' | 'info'): string {
  const classes = {
    success: 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200',
    error: 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200',
    info: 'bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
  }
  return classes[type]
}

// Export for standalone use
export { getMessageClasses }

